import { HIDDEN_VIEW_NAMES } from '@/shared/config/view-permissions'

/**
 * Pure function to build navigation tree from view permissions
 * Used by both navigation store and navigationMachine
 */

export interface ViewPermissionLike {
  id: string
  name: string
  parentId?: string | null
  order: number
}

export interface NavigationItemLike {
  id: string
  name: string
  nameRu: string
  title?: string
  type?: string
  link?: string
  icon?: string
  children?: NavigationItemLike[]
  order: number
  viewName?: string
  subtitle?: string
}

export type GetDefinitionFn = (viewName: string) => Partial<NavigationItemLike> | undefined

export function buildNavigationFromPermissions(
  viewPermissions: ViewPermissionLike[],
  rootViewName: string,
  getDefinition: GetDefinitionFn
): NavigationItemLike[] {
  const rootView = viewPermissions.find((v) => v.name === rootViewName)
  if (!rootView) {
    console.error('[navigation-build] Root view not found:', rootViewName)
    return []
  }

  const buildTree = (parentId: string | null | undefined): NavigationItemLike[] => {
    const children = viewPermissions
      .filter((v) => v.parentId === parentId)
      .filter((v) => !HIDDEN_VIEW_NAMES.has(v.name))
      .sort((a, b) => a.order - b.order)

    return (children
      .map((view) => {
        const definition = getDefinition(view.name) || {}
        if (definition.link?.includes('/hr/rotations')) return null
        const childItems = buildTree(view.id)

        return {
          id: view.id,
          name: definition.name || view.name,
          nameRu: (definition.nameRu as string) || view.name,
          link: definition.link,
          icon: definition.icon,
          type: definition.type,
          order: view.order,
          viewName: view.name,
          ...(childItems.length > 0 && { children: childItems }),
        }
      })
      .filter((item) => item != null)) as NavigationItemLike[]
  }

  return buildTree(rootView.id)
}
